/* eslint-disable react-refresh/only-export-components -- context module pairs a provider with its hook */
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { progressRepository } from '../persistence/progressRepository'

/**
 * Lesson State boundary
 * ---------------------
 * The single seam between interaction components and persistence. Components
 * read their saved response and report changes/completion *through this layer* —
 * they never touch localStorage. The provider persists responses and completion
 * via the progress repository, so state restores after a refresh, and marks the
 * whole lesson complete once every activity is done.
 *
 * A default in-memory implementation backs the context, so interactions still
 * work (without persistence) when rendered outside a provider, e.g. in previews
 * and unit tests.
 */
export interface LessonState {
  getResponse(interactionId: string): unknown
  setResponse(interactionId: string, response: unknown): void
  isComplete(interactionId: string): boolean
  markComplete(interactionId: string): void
  completedCount: number
  totalCount: number
  isLessonComplete: boolean
  /** First activity not yet completed — used to resume where the learner left off. */
  firstIncompleteId: string | null
}

function createInMemoryLessonState(): LessonState {
  const responses = new Map<string, unknown>()
  const complete = new Set<string>()
  return {
    getResponse: (id) => responses.get(id),
    setResponse: (id, response) => {
      responses.set(id, response)
    },
    isComplete: (id) => complete.has(id),
    markComplete: (id) => {
      complete.add(id)
    },
    completedCount: 0,
    totalCount: 0,
    isLessonComplete: false,
    firstIncompleteId: null,
  }
}

const LessonStateContext = createContext<LessonState>(createInMemoryLessonState())

export function useLessonState(): LessonState {
  return useContext(LessonStateContext)
}

/**
 * Provides persisted lesson state for a single lesson. `interactionIds` is the
 * ordered list of activities in the lesson, used to compute progress, the
 * resume point, and whole-lesson completion.
 */
export function LessonStateProvider({
  lessonId,
  interactionIds,
  children,
}: {
  lessonId: string
  interactionIds: string[]
  children: ReactNode
}) {
  const initial = progressRepository.getLessonProgress(lessonId)
  const [responses, setResponses] = useState<Record<string, unknown>>(
    () => initial?.responses ?? {},
  )
  const [completed, setCompleted] = useState<Set<string>>(
    () => new Set(initial?.completedInteractions ?? []),
  )

  const value = useMemo<LessonState>(() => {
    const completedCount = interactionIds.filter((id) => completed.has(id)).length
    const totalCount = interactionIds.length
    return {
      getResponse: (id) => responses[id],
      setResponse: (id, response) => {
        setResponses((prev) => ({ ...prev, [id]: response }))
        progressRepository.setLessonResponse(lessonId, id, response)
      },
      isComplete: (id) => completed.has(id),
      markComplete: (id) => {
        if (completed.has(id)) return
        const next = new Set(completed).add(id)
        setCompleted(next)
        progressRepository.setInteractionComplete(lessonId, id)
        if (interactionIds.length > 0 && interactionIds.every((entry) => next.has(entry))) {
          progressRepository.setLessonStatus(lessonId, 'completed')
        }
      },
      completedCount,
      totalCount,
      isLessonComplete: totalCount > 0 && completedCount === totalCount,
      firstIncompleteId: interactionIds.find((id) => !completed.has(id)) ?? null,
    }
  }, [responses, completed, interactionIds, lessonId])

  return <LessonStateContext.Provider value={value}>{children}</LessonStateContext.Provider>
}
